import React, { useState, useMemo } from 'react';
import {
  DollarSign,
  CreditCard,
  ArrowUpRight,
  ArrowDownLeft,
  Clock,
  Hash,
  CheckCircle,
  XCircle,
  Link2,
  Banknote,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { SectionCard, MetricCard, TabLoading, TabError, TabEmpty, StatusBadge, getGenericStatusVariant } from './shared';
import { useUserPayments } from '../../hooks/useUserTabs';
import { cn, formatDateTime, formatRelativeTime, formatNumber, formatCents, formatCentsDecimal } from '../../utils/helpers';
import type { LedgerEntryItem, PayoutItem } from '../../types/userTabs';

interface FinancialTabProps {
  userId: number;
  stripeCustomerId?: string | null;
  stripeAccountId?: string | null;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  totalSpentCents?: number;
  totalEarnedCents?: number;
  completedBookingsAsCustomer?: number;
  completedBookingsAsProvider?: number;
}

// ============================================================================
// Helpers
// ============================================================================

function formatDollars(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

function getLedgerEntryTypeVariant(type: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (type) {
    case 'PROVIDER_PAYABLE': case 'PAYOUT': return 'success';
    case 'CUSTOMER_CHARGE': return 'info';
    case 'REFUND': return 'warning';
    case 'PENALTY_CHARGE': return 'error';
    default: return 'default';
  }
}

function getPayoutStatusVariant(status: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (status.toUpperCase()) {
    case 'PAID': return 'success';
    case 'PENDING': case 'REQUESTED': return 'warning';
    case 'SUBMITTED': case 'IN_TRANSIT': return 'info';
    case 'FAILED': case 'CANCELLED': return 'error';
    default: return 'default';
  }
}

type LedgerFilter = 'all' | 'PROVIDER_PAYABLE' | 'CUSTOMER_CHARGE' | 'REFUND' | 'PENALTY_CHARGE';

// ============================================================================
// Ledger Entry Row
// ============================================================================

const LedgerEntryRow: React.FC<{ entry: LedgerEntryItem }> = ({ entry }) => {
  const isCredit = entry.entryType === 'PROVIDER_PAYABLE' || entry.entryType === 'REFUND';
  return (
    <div className="flex items-start justify-between gap-3 py-3 border-b border-border/30 last:border-0">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className={cn(
          'p-1.5 rounded-lg flex-shrink-0',
          isCredit ? 'bg-emerald-500/10' : 'bg-orange-500/10'
        )}>
          {isCredit ? (
            <ArrowDownLeft className={cn('h-3.5 w-3.5', 'text-emerald-400')} />
          ) : (
            <ArrowUpRight className={cn('h-3.5 w-3.5', 'text-orange-400')} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge
              status={entry.entryType.replace(/_/g, ' ')}
              variant={getLedgerEntryTypeVariant(entry.entryType)}
            />
            <StatusBadge
              status={entry.status}
              variant={getGenericStatusVariant(entry.status)}
            />
          </div>
          {entry.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{entry.description}</p>
          )}
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 text-[10px] text-muted-foreground">
            <span className="font-mono">#{entry.id}</span>
            {entry.bookingId && <span>Booking #{entry.bookingId}</span>}
            {entry.transactionId && (
              <span className="font-mono" title={entry.transactionId}>
                {entry.transactionId.length > 20
                  ? `${entry.transactionId.slice(0, 20)}...`
                  : entry.transactionId}
              </span>
            )}
            <span className="flex items-center gap-1" title={formatDateTime(entry.createdAt)}>
              <Clock className="h-3 w-3" />
              {formatRelativeTime(entry.createdAt)}
            </span>
          </div>
        </div>
      </div>
      <div className="flex-shrink-0 text-right">
        <p className={cn(
          'text-sm font-bold',
          isCredit ? 'text-emerald-400' : 'text-foreground'
        )}>
          {isCredit ? '+' : ''}{formatDollars(entry.amount)}
        </p>
        <p className="text-[10px] text-muted-foreground">{entry.currency}</p>
      </div>
    </div>
  );
};

// ============================================================================
// Payout Card
// ============================================================================

const PayoutCard: React.FC<{ payout: PayoutItem }> = ({ payout }) => (
  <div className="rounded-lg border border-border/50 bg-muted/5 p-4 space-y-2.5">
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="p-1.5 rounded-lg bg-emerald-500/10 flex-shrink-0">
          <Banknote className="h-3.5 w-3.5 text-emerald-400" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <StatusBadge status={payout.status} variant={getPayoutStatusVariant(payout.status)} />
            <span className="text-[10px] text-muted-foreground font-mono">#{payout.id}</span>
          </div>
          {payout.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{payout.description}</p>
          )}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-bold text-foreground">{formatDollars(payout.amount)}</p>
        <p className="text-[10px] text-muted-foreground">{payout.currency}</p>
      </div>
    </div>

    {/* Fee breakdown */}
    <div className="flex items-center gap-4 pl-9 text-[11px] text-muted-foreground">
      <span>Platform fee: {formatDollars(payout.platformFee)}</span>
      <span className="font-medium text-foreground">Net: {formatDollars(payout.netAmount)}</span>
      {payout.bookingId && <span>Booking #{payout.bookingId}</span>}
      <span className="capitalize">{payout.type.toLowerCase()}</span>
    </div>

    {/* Stripe IDs */}
    {(payout.stripeTransferId || payout.stripePayoutId) && (
      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 pl-9 text-[10px] text-muted-foreground font-mono">
        {payout.stripeTransferId && (
          <span title={payout.stripeTransferId}>Transfer: {payout.stripeTransferId.slice(0, 20)}...</span>
        )}
        {payout.stripePayoutId && (
          <span title={payout.stripePayoutId}>Payout: {payout.stripePayoutId.slice(0, 20)}...</span>
        )}
      </div>
    )}

    {/* Timeline */}
    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 pl-9 text-[10px] text-muted-foreground">
      {payout.requestedAt && (
        <span title={formatDateTime(payout.requestedAt)}>
          Requested {formatRelativeTime(payout.requestedAt)}
        </span>
      )}
      {payout.submittedAt && (
        <span title={formatDateTime(payout.submittedAt)}>
          Submitted {formatRelativeTime(payout.submittedAt)}
        </span>
      )}
      {payout.paidAt && (
        <span className="text-emerald-400" title={formatDateTime(payout.paidAt)}>
          Paid {formatRelativeTime(payout.paidAt)}
        </span>
      )}
      {payout.failedAt && (
        <span className="text-red-400" title={formatDateTime(payout.failedAt)}>
          Failed {formatRelativeTime(payout.failedAt)}
        </span>
      )}
      <span title={formatDateTime(payout.createdAt)}>
        <Clock className="h-3 w-3 inline mr-0.5" />
        {formatRelativeTime(payout.createdAt)}
      </span>
    </div>

    {/* Failure info */}
    {payout.failureMessage && (
      <div className="flex items-start gap-2 pl-9 mt-1">
        <AlertTriangle className="h-3 w-3 text-red-400 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-red-400">
          {payout.failureCode && <span className="font-mono mr-1">[{payout.failureCode}]</span>}
          {payout.failureMessage}
        </p>
      </div>
    )}
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

const FinancialTab: React.FC<FinancialTabProps> = ({
  userId,
  stripeCustomerId,
  stripeAccountId,
  chargesEnabled,
  payoutsEnabled,
  totalSpentCents,
  totalEarnedCents,
  completedBookingsAsCustomer,
  completedBookingsAsProvider,
}) => {
  const { data, loading, error, refetch } = useUserPayments(userId, true);
  const [ledgerFilter, setLedgerFilter] = useState<LedgerFilter>('all');

  const ledgerItems = data?.ledgerEntries?.items ?? [];
  const ledgerTotal = data?.ledgerEntries?.total ?? 0;
  const payoutItems = data?.payouts?.items ?? [];
  const payoutTotal = data?.payouts?.total ?? 0;

  const filteredLedger = useMemo(() => {
    if (ledgerFilter === 'all') return ledgerItems;
    return ledgerItems.filter((e) => e.entryType === ledgerFilter);
  }, [ledgerItems, ledgerFilter]);

  const totalPayoutAmount = useMemo(
    () => payoutItems.reduce((sum, p) => sum + p.netAmount, 0),
    [payoutItems]
  );

  const totalPlatformFees = useMemo(
    () => payoutItems.reduce((sum, p) => sum + p.platformFee, 0),
    [payoutItems]
  );

  if (loading) return <TabLoading />;
  if (error) return <TabError message={error} onRetry={refetch} />;

  const ledgerFilters: { key: LedgerFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'PROVIDER_PAYABLE', label: 'Payable' },
    { key: 'CUSTOMER_CHARGE', label: 'Charges' },
    { key: 'REFUND', label: 'Refunds' },
    { key: 'PENALTY_CHARGE', label: 'Penalties' },
  ];

  return (
    <div className="space-y-5">
      {/* KPI Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard
          label="Total Spent"
          value={totalSpentCents != null ? formatCents(totalSpentCents) : 'N/A'}
          icon={CreditCard}
          color="text-orange-400"
          bgColor="bg-orange-400/10"
          subtitle={completedBookingsAsCustomer != null ? `${formatNumber(completedBookingsAsCustomer)} bookings` : undefined}
        />
        <MetricCard
          label="Total Earned"
          value={totalEarnedCents != null ? formatCents(totalEarnedCents) : 'N/A'}
          icon={DollarSign}
          color="text-emerald-400"
          bgColor="bg-emerald-400/10"
          subtitle={completedBookingsAsProvider != null ? `${formatNumber(completedBookingsAsProvider)} bookings` : undefined}
        />
        <MetricCard
          label="Ledger Entries"
          value={formatNumber(ledgerTotal)}
          icon={FileText}
          color="text-blue-400"
          bgColor="bg-blue-400/10"
        />
        <MetricCard
          label="Payouts"
          value={formatNumber(payoutTotal)}
          icon={Banknote}
          color="text-violet-400"
          bgColor="bg-violet-400/10"
          subtitle={payoutItems.length > 0 ? `${formatDollars(totalPayoutAmount)} net` : undefined}
        />
      </div>

      {/* Stripe Configuration */}
      {(stripeCustomerId || stripeAccountId) && (
        <SectionCard title="Stripe Configuration" icon={CreditCard} collapsible>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0">
            <div className="flex items-center justify-between py-2.5 border-b border-border/30">
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                <CreditCard className="h-3 w-3" />
                Customer ID
              </span>
              <span className="text-xs text-foreground font-mono">{stripeCustomerId || 'N/A'}</span>
            </div>
            <div className="flex items-center justify-between py-2.5 border-b border-border/30">
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Link2 className="h-3 w-3" />
                Connect Account ID
              </span>
              <span className="text-xs text-foreground font-mono">{stripeAccountId || 'N/A'}</span>
            </div>
            <div className="flex items-center justify-between py-2.5 border-b border-border/30">
              <span className="text-xs text-muted-foreground">Charges Enabled</span>
              <div className="flex items-center gap-1.5">
                {chargesEnabled ? (
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-red-400" />
                )}
                <span className="text-xs">{chargesEnabled ? 'Yes' : 'No'}</span>
              </div>
            </div>
            <div className="flex items-center justify-between py-2.5 border-b border-border/30">
              <span className="text-xs text-muted-foreground">Payouts Enabled</span>
              <div className="flex items-center gap-1.5">
                {payoutsEnabled ? (
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-red-400" />
                )}
                <span className="text-xs">{payoutsEnabled ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Ledger Entries */}
      <SectionCard
        title="Ledger Entries"
        icon={FileText}
        subtitle={`${ledgerTotal} total entries`}
        collapsible
        defaultOpen
      >
        {/* Filter pills */}
        <div className="flex items-center gap-1 p-1 bg-muted/10 rounded-lg w-fit mb-3">
          {ledgerFilters.map((f) => (
            <button
              key={f.key}
              onClick={() => setLedgerFilter(f.key)}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                ledgerFilter === f.key
                  ? 'bg-primary/15 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {filteredLedger.length > 0 ? (
          <div>
            {filteredLedger.map((entry) => (
              <LedgerEntryRow key={entry.id} entry={entry} />
            ))}
          </div>
        ) : (
          <div className="py-8 text-center">
            <FileText className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">
              {ledgerFilter === 'all' ? 'No ledger entries' : `No ${ledgerFilter.replace(/_/g, ' ').toLowerCase()} entries`}
            </p>
          </div>
        )}
      </SectionCard>

      {/* Payouts */}
      <SectionCard
        title="Payouts"
        icon={Banknote}
        subtitle={`${payoutTotal} total${totalPlatformFees > 0 ? ` · ${formatDollars(totalPlatformFees)} in fees` : ''}`}
        collapsible
        defaultOpen={payoutItems.length > 0}
      >
        {payoutItems.length > 0 ? (
          <div className="space-y-3">
            {payoutItems.map((payout) => (
              <PayoutCard key={payout.id} payout={payout} />
            ))}
          </div>
        ) : (
          <div className="py-8 text-center">
            <Banknote className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">No payouts</p>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default FinancialTab;
